import DoubleArrowIcon from '@mui/icons-material/DoubleArrow';
import HomeIcon from '@mui/icons-material/Home';
import FileCopyIcon from '@mui/icons-material/FileCopy';
import PeopleIcon from '@mui/icons-material/People';
import MoneyIcon from '@mui/icons-material/Money';
import AssignmentIcon from '@mui/icons-material/Assignment'; // Import the tasks icon
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import SendIcon from '@mui/icons-material/Send';
import HowToRegIcon from '@mui/icons-material/HowToReg';
import ReceiptIcon from '@mui/icons-material/Receipt';
import { Drawer, List, ListItem, ListItemIcon, ListItemText, IconButton, Box, Tooltip } from '@mui/material';
import { CalendarViewDayOutlined } from '@mui/icons-material';
import PersonIcon from '@mui/icons-material/Person'; // Import the user icon

import MailOutlineIcon from '@mui/icons-material/MailOutline';

import { useSelector } from 'react-redux'; // Import useSelector
import DescriptionIcon from '@mui/icons-material/Description'; // Import the documents icon
import AssessmentIcon from '@mui/icons-material/Assessment'; // Import the report icon
import HistoryIcon from '@mui/icons-material/History'; // Import the activity logging icon
import SettingsIcon from '@mui/icons-material/Settings'; // Import the settings icon
import AccountTreeIcon from '@mui/icons-material/AccountTree'; // Import the workflows icon
import { useNavigate, useLocation } from 'react-router-dom'; // Import useLocation
import { smallTitle, thinBold, thinTitle } from '../styles/typography';
import FolderCopyIcon from '@mui/icons-material/FolderCopy';
import FormatListNumberedIcon from '@mui/icons-material/FormatListNumbered';
import TodayIcon from '@mui/icons-material/Today';
import { useTranslation } from 'react-i18next';
import React from 'react';

const Sidebar = React.memo(({ open, setOpen }) => {
  const { t } = useTranslation('navigation');
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useSelector((state) => state.userLoaded); // Get user from Redux state

  const menuItems = [
    { icon: <HomeIcon />, text: t('home'), path: '/dashboard' },
    { icon: <PeopleIcon />, text: t('relationships'), path: '/relationships' },
    { icon: <FolderCopyIcon />, text: t('files'), path: '/files' },
    { icon: <DescriptionIcon />, text: t('documents'), path: '/documents' }, // Add the documents menu item
    { icon: <FormatListNumberedIcon />, text: t('tasks'), path: '/tasks' }, // Add the tasks menu item
    { icon: <AccessTimeIcon />, text: t('timesheets'), path: '/timesheets' },
    { icon: <ReceiptIcon />, text: t('disbursements'), path: '/disbursements' },
    { icon: <TodayIcon />, text: t('calendar'), path: '/calendar' }, // Add the tasks menu item
    { icon: <MoneyIcon />, text: t('finance'), path: '/finance' },
    { icon: <MailOutlineIcon />, text: t('emails'), path: '/emails' },
    { icon: <AssessmentIcon />, text: t('reports'), path: '/report-generator' }, // Add the report menu item
  ];

  // Conditionally add Workflows, Settings, User Management and Activity Logging menu items if the user is a Manager
  if (user && user.role === 'Manager') {
    menuItems.push(
      { icon: <AccountTreeIcon />, text: t('workflows'), path: '/workflows' },
      { icon: <SettingsIcon />, text: t('settings'), path: '/settings' },
      { icon: <PersonIcon />, text: t('userAccess'), path: '/user-management' },
      { icon: <HistoryIcon />, text: t('activityLogging'), path: '/activity-logging' }
    );
  }

  const toggleDrawer = () => {
    setOpen(!open);
  };

  return (
    <Drawer
      variant="permanent"
      sx={{
        flexShrink: 0,
        width: open ? '200px' : '60px',
        color: '#ECF0F1', // Sidebar text and icon color
        '& .MuiDrawer-paper': {
          width: open ? '200px' : '60px',
          transition: 'width 0.3s ease',
          overflowX: 'hidden',
          backgroundColor: '#0B212F', // Sidebar background color
          color: '#ECF0F1', // Sidebar text and icon color
        },
      }}
    >
      <List>
        {menuItems.map((item, index) => (
          <Tooltip title={item.text} key={index} placement="right" disableHoverListener={open}>
            <ListItem
              button
              onClick={() => navigate(item.path)}
              sx={{
                ...thinBold,
                cursor: 'pointer',
                backgroundColor: location.pathname === item.path ? '#2B80B6' : '#0B212F',
                minHeight: '40px',
                padding: '0 16px',
                '&:hover': {
                  backgroundColor: location.pathname === item.path ? '#2C6B8C' : '#0D3D51',
                },
              }}
            >
              <ListItemIcon sx={{ color: '#ECF0F1', minWidth: '36px' }}>{item.icon}</ListItemIcon>
              {open && <ListItemText primary={item.text} primaryTypographyProps={{...thinTitle, fontSize: '0.9rem'}} />}
            </ListItem>
          </Tooltip>
        ))}
      </List>
      <Box sx={{ flexGrow: 1 }} />
      <div style={{ display: 'flex', justifyContent: open ? 'flex-end' : 'center', alignItems: 'center', height: '60px' }}>
        <Tooltip title={open ? t('sidebar.collapse') : t('sidebar.expand')} placement="left">
          <IconButton
            onClick={toggleDrawer}
            sx={{
              color: '#ECF0F1',
              '&:hover': {
                backgroundColor: '#0D3D51',
              },
            }}
          >
            <DoubleArrowIcon sx={{ transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 0.3s ease' }} />
          </IconButton>
        </Tooltip>
      </div>
    </Drawer>
  );
});

Sidebar.displayName = 'Sidebar';

export default Sidebar;